import React, { useEffect } from 'react';
import { Chapter } from '../data/bookContent';
import { Button } from './ui/Button';
import { ProgressBar } from './ProgressBar';
import { ArrowLeftIcon, ArrowRightIcon, ListIcon } from 'lucide-react';
interface ChapterViewProps {
  chapter: Chapter;
  currentChapterIndex: number;
  totalChapters: number;
  onNext: () => void;
  onPrev: () => void;
  onToC: () => void;
}
export function ChapterView({
  chapter,
  currentChapterIndex,
  totalChapters,
  onNext,
  onPrev,
  onToC
}: ChapterViewProps) {
  // Scroll to top when chapter changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [chapter.id]);
  const isFirst = currentChapterIndex === 0;
  const isLast = currentChapterIndex === totalChapters - 1;
  return <div className="min-h-screen bg-stone-50 flex flex-col" data-id="element-165">
      {/* Navigation Header */}
      <header className="sticky top-0 z-20 bg-stone-50/95 backdrop-blur border-b border-stone-200 shadow-sm" data-id="element-166">
        <div className="max-w-3xl mx-auto px-4 py-3" data-id="element-167">
          <div className="flex items-center justify-between mb-2" data-id="element-168">
            <Button variant="ghost" onClick={onToC} aria-label="Volver al índice" data-id="element-169">
              <ListIcon className="h-6 w-6 mr-2" data-id="element-170" />
              <span className="text-lg" data-id="element-171">Índice</span>
            </Button>
            <span className="text-stone-500 font-medium" data-id="element-172">
              {currentChapterIndex + 1} de {totalChapters}
            </span>
          </div>
          <ProgressBar current={currentChapterIndex} total={totalChapters} data-id="element-173" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-10 md:py-16" data-id="element-174">
        <article className="prose prose-stone prose-lg md:prose-xl max-w-none" data-id="element-175">
          <span className="text-amber-700 font-bold tracking-widest uppercase text-sm md:text-base" data-id="element-176">
            Capítulo {chapter.id}
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-stone-900 mt-2 mb-8 leading-tight" data-id="element-177">
            {chapter.title}
          </h1>

          {/* Key Points Cards */}
          <div className="grid gap-6 mb-12 not-prose" data-id="element-178">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl" data-id="element-179">
              <h3 className="text-amber-900 font-bold text-lg mb-2 uppercase tracking-wide" data-id="element-180">
                Objetivo
              </h3>
              <p className="text-stone-800 text-xl" data-id="element-181">{chapter.objective}</p>
            </div>

            <div className="bg-white border border-stone-200 p-6 rounded-xl shadow-sm" data-id="element-182">
              <h3 className="text-stone-500 font-bold text-sm mb-2 uppercase tracking-wide" data-id="element-183">
                Ejemplo Práctico
              </h3>
              <p className="text-stone-800 text-xl italic font-serif" data-id="element-184">
                "{chapter.example}"
              </p>
            </div>
          </div>

          {/* Main Text Content */}
          <div className="space-y-6 text-stone-800 leading-relaxed text-xl md:text-2xl font-sans" data-id="element-185">
            {chapter.content.map((paragraph, idx) => <p key={idx} data-id="element-186">{paragraph}</p>)}
          </div>

          {/* Framework / Action Item */}
          <div className="mt-12 bg-stone-900 text-stone-50 p-8 rounded-2xl shadow-xl not-prose" data-id="element-187">
            <h3 className="text-amber-400 font-bold text-lg mb-4 uppercase tracking-wide flex items-center" data-id="element-188">
              <span className="mr-2" data-id="element-189">✨</span> Tu Herramienta
            </h3>
            <p className="text-xl md:text-2xl font-medium leading-relaxed" data-id="element-190">
              {chapter.framework}
            </p>
          </div>
        </article>
      </main>

      {/* Footer Navigation */}
      <footer className="bg-white border-t border-stone-200 py-8 px-6 mt-auto" data-id="element-191">
        <div className="max-w-3xl mx-auto flex justify-between gap-4" data-id="element-192">
          <Button variant="secondary" onClick={onPrev} disabled={isFirst} className={isFirst ? 'invisible' : ''} aria-label="Capítulo anterior" data-id="element-193">
            <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-194" />
            Anterior
          </Button>

          <Button variant="primary" onClick={isLast ? onToC : onNext} className="flex-1 max-w-xs" aria-label={isLast ? 'Finalizar libro' : 'Siguiente capítulo'} data-id="element-195">
            {isLast ? 'Finalizar' : 'Siguiente'}
            {!isLast && <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-196" />}
          </Button>
        </div>
      </footer>
    </div>;
}